import { useEffect, useRef, useState } from 'react';
import { LargeLogo } from '../assets/images';
import '../styles/Navbar.css';

// Hamburger menu icon
const HamburgerIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
    </svg>
);

// Close icon for mobile menu
const CloseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
    </svg>
);

// Small caret for the dropdown trigger (update 2026_07_13.2)
const CaretIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
        <path d="M7 10l5 5 5-5z"/>
    </svg>
);

interface NavItem {
    label: string;
    url?: string;
    // update 2026_07_13.2 - Care Providers expands into sub choices
    children?: { label: string; url: string }[];
}

const Navbar = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const navItems: NavItem[] = [
        { label: 'Home', url: 'https://www.openscore.org.uk/' },
        { label: 'The Data', url: 'https://www.openscore.org.uk/data' },
        { label: 'Care Seekers', url: 'https://www.openscore.org.uk/care-seekers' },
        {
            label: 'Care Providers',
            children: [
                { label: 'Understand OpenScore', url: 'https://www.openscore.org.uk/care-homes' },
                { label: 'Add your first event', url: '/submit-event' },
                { label: 'Manage my events', url: '/my-events' }
            ]
        }
    ];

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const handleNavClick = (url: string) => {
        window.open(url, '_self', 'noopener,noreferrer');
        setIsMobileMenuOpen(false); // Close mobile menu after navigation
        setOpenSubmenu(null);
    };

    // Close an open desktop submenu when clicking anywhere outside the menu.
    useEffect(() => {
        if (!openSubmenu) return;
        const handleOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setOpenSubmenu(null);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [openSubmenu]);

    return (
        <nav className="navbar">
            <div className="navbar-container">
                {/* Logo section */}
                <div className="navbar-logo">
                    <img 
                        src={LargeLogo} 
                        alt="Open Score Logo" 
                        className="logo-image"
                    />
                </div>

                {/* Right side container for menu and mobile toggle */}
                <div className="navbar-right">
                    {/* Desktop navigation menu */}
                    <div className="navbar-menu" ref={menuRef}>
                        {navItems.map((item) => (
                            item.children ? (
                                <div
                                    key={item.label}
                                    className="navbar-dropdown"
                                    onMouseEnter={() => setOpenSubmenu(item.label)}
                                    onMouseLeave={() => setOpenSubmenu(null)}
                                >
                                    <button
                                        className="navbar-item"
                                        onClick={() => setOpenSubmenu(openSubmenu === item.label ? null : item.label)}
                                        aria-label={item.label}
                                        aria-haspopup="true"
                                        aria-expanded={openSubmenu === item.label}
                                    >
                                        <span>{item.label}</span>
                                        <CaretIcon />
                                    </button>
                                    {openSubmenu === item.label && (
                                        <div className="navbar-dropdown-menu" role="menu">
                                            {item.children.map((child) => (
                                                <button
                                                    key={child.label}
                                                    className="navbar-dropdown-item"
                                                    role="menuitem"
                                                    onClick={() => handleNavClick(child.url)}
                                                    aria-label={child.label}
                                                >
                                                    {child.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <button
                                    key={item.label}
                                    className="navbar-item"
                                    onClick={() => handleNavClick(item.url!)}
                                    aria-label={item.label}
                                >
                                    <span>{item.label}</span>
                                </button>
                            )
                        ))}
                    </div>

                    {/* Mobile menu toggle */}
                    <button 
                        className="mobile-menu-toggle"
                        onClick={toggleMobileMenu}
                        aria-label="Toggle mobile menu"
                        aria-expanded={isMobileMenuOpen}
                    >
                        {isMobileMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
                    </button>
                </div>
            </div>

            {/* Mobile navigation menu */}
            <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
                {navItems.map((item) => (
                    item.children ? (
                        <div key={item.label} className="mobile-menu-group">
                            <span className="mobile-menu-group-label">{item.label}</span>
                            {item.children.map((child) => (
                                <button
                                    key={child.label}
                                    className="mobile-menu-item mobile-menu-subitem"
                                    onClick={() => handleNavClick(child.url)}
                                    aria-label={child.label}
                                >
                                    <span>{child.label}</span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <button
                            key={item.label}
                            className="mobile-menu-item"
                            onClick={() => handleNavClick(item.url!)}
                            aria-label={item.label}
                        >
                            <span>{item.label}</span>
                        </button>
                    )
                ))}
            </div>
        </nav>
    );
};

export default Navbar;
